import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, differenceInDays, addDays, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { slideService } from '../../services/slideService';
import { getLogoUrl } from '../../services/logoService';
import Icon from '../../components/AppIcon';

// ─── Builder Slide: Section Renderers ─────────────────────────────────────────

const REMINDER_STYLE_MAP = {
  safety: { bg: 'bg-red-600', text: 'text-white', icon: 'ShieldAlert', border: 'border-red-400' },
  info: { bg: 'bg-blue-600', text: 'text-white', icon: 'Info', border: 'border-blue-400' },
  warning: { bg: 'bg-amber-500', text: 'text-white', icon: 'AlertTriangle', border: 'border-amber-300' },
  success: { bg: 'bg-green-600', text: 'text-white', icon: 'CheckCircle', border: 'border-green-400' },
  dark: { bg: 'bg-gray-900', text: 'text-white', icon: 'Monitor', border: 'border-gray-600' },
};

const TEXT_SIZE_MAP = {
  sm: { heading: 'text-2xl', body: 'text-base' },
  md: { heading: 'text-3xl', body: 'text-xl' },
  lg: { heading: 'text-4xl', body: 'text-2xl' },
  xl: { heading: 'text-5xl', body: 'text-3xl' },
};

const ReminderSection = ({ config }) => {
  const style = REMINDER_STYLE_MAP?.[config?.style || 'safety'] || REMINDER_STYLE_MAP?.safety;
  return (
    <div className={`${style?.bg} ${style?.text} rounded-2xl p-6 flex items-center gap-5 border-2 ${style?.border} shadow-lg`}>
      <div className="flex-shrink-0">
        <Icon name={style?.icon} size={48} color="#fff" />
      </div>
      <div>
        <p className="text-3xl font-black tracking-tight leading-tight">{config?.message || 'Reminder'}</p>
        {config?.subtext && (
          <p className="text-lg mt-1 opacity-80">{config?.subtext}</p>
        )}
      </div>
    </div>
  );
};

const TextSection = ({ config }) => {
  const sizes = TEXT_SIZE_MAP?.[config?.textSize || 'md'] || TEXT_SIZE_MAP?.md;
  const color = config?.textColor || '#ffffff';
  return (
    <div className="py-2">
      {config?.heading && (
        <h3 className={`${sizes?.heading} font-bold mb-2 leading-tight`} style={{ color }}>
          {config?.heading}
        </h3>
      )}
      {config?.body && (
        <p className={`${sizes?.body} leading-relaxed opacity-90`} style={{ color }}>
          {config?.body}
        </p>
      )}
    </div>
  );
};

const ImagesSection = ({ config }) => {
  const images = config?.images || [];
  const layout = config?.layout || 'single';

  if (images?.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 bg-white/10 rounded-xl">
        <Icon name="Images" size={40} color="#9ca3af" />
      </div>
    );
  }

  // Freeform layout: render images at their saved x/y/w/h positions
  if (layout === 'freeform') {
    const CANVAS_W = 560;
    const CANVAS_H = 315;
    return (
      <div className="relative w-full" style={{ paddingBottom: `${(CANVAS_H / CANVAS_W) * 100}%` }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {images?.map((img, idx) => {
            const x = img?.x ?? (idx * 30);
            const y = img?.y ?? (idx * 20);
            const w = img?.w ?? 200;
            const h = img?.h ?? 140;
            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${(x / CANVAS_W) * 100}%`,
                  top: `${(y / CANVAS_H) * 100}%`,
                  width: `${(w / CANVAS_W) * 100}%`,
                  height: `${(h / CANVAS_H) * 100}%`,
                  overflow: 'hidden',
                  borderRadius: 8,
                }}
              >
                <img
                  src={img?.url}
                  alt={img?.caption || `Image ${idx + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {img?.caption && (
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                    color: '#fff', padding: '4px 8px', fontSize: '0.75rem', fontWeight: 500,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {img?.caption}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const gridClass = {
    single: 'grid-cols-1',
    two: 'grid-cols-2',
    three: 'grid-cols-3',
    grid: 'grid-cols-2',
  }?.[layout] || 'grid-cols-1';

  const displayImages = layout === 'grid' ? images?.slice(0, 4) : layout === 'three' ? images?.slice(0, 3) : layout === 'two' ? images?.slice(0, 2) : images?.slice(0, 1);

  return (
    <div className={`grid ${gridClass} gap-3`}>
      {displayImages?.map((img, idx) => (
        <div key={idx} className="relative rounded-xl overflow-hidden bg-gray-800" style={{ aspectRatio: layout === 'single' ? '16/7' : '4/3' }}>
          <img
            src={img?.url}
            alt={img?.caption || `Image ${idx + 1}`}
            className="w-full h-full object-cover"
          />
          {img?.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
              <p className="text-white text-sm font-medium truncate">{img?.caption}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const GanttSection = ({ config, projects, allDeliverables }) => {
  const projectId = config?.projectId || projects?.[0]?.id;
  const project = projects?.find(p => p?.id === projectId);
  const viewMode = config?.viewMode || 'weekly';
  const fields = config?.fields || ['phases', 'deliverables'];
  const maxRows = config?.maxRows || 12;

  const deliverables = (allDeliverables || [])?.filter(d => d?.project_id === projectId);

  const rowTypeMap = {
    phases: 'phase',
    deliverables: 'deliverable',
    tasks: 'task',
    subtasks: 'subtask',
  };

  const allowedTypes = fields?.map(f => rowTypeMap?.[f])?.filter(Boolean);

  const items = deliverables
    ?.filter(d => d?.start_date && d?.end_date && allowedTypes?.includes(d?.row_type || 'deliverable'))
    ?.slice(0, maxRows);

  if (!project || items?.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 bg-white/10 rounded-xl">
        <div className="text-center text-white/50">
          <Icon name="GitBranch" size={28} color="#9ca3af" />
          <p className="text-sm mt-1">{!project ? 'No project selected' : 'No dated items found'}</p>
        </div>
      </div>
    );
  }

  const dates = items?.flatMap(d => [new Date(d?.start_date), new Date(d?.end_date)])?.filter(Boolean);
  const minDate = new Date(Math.min(...dates?.map(d => d?.getTime())));
  const maxDate = new Date(Math.max(...dates?.map(d => d?.getTime())));
  const totalDays = Math.max(differenceInDays(maxDate, minDate) + 1, 1);

  // Build column headers based on viewMode
  let columns = [];
  if (viewMode === 'daily') {
    const count = Math.min(totalDays, 30);
    columns = Array.from({ length: count }, (_, i) => addDays(minDate, i));
  } else if (viewMode === 'weekly') {
    columns = eachWeekOfInterval({ start: minDate, end: maxDate });
  } else {
    columns = eachMonthOfInterval({ start: minDate, end: maxDate });
  }

  const typeColors = {
    phase: 'bg-amber-500',
    deliverable: 'bg-purple-500',
    task: 'bg-blue-500',
    subtask: 'bg-emerald-500',
  };

  const getBarStyle = (item) => {
    const start = new Date(item?.start_date);
    const end = new Date(item?.end_date);
    const left = (differenceInDays(start, minDate) / totalDays) * 100;
    const width = Math.max(((differenceInDays(end, start) + 1) / totalDays) * 100, 0.5);
    return { left: `${left}%`, width: `${width}%` };
  };

  const formatCol = (d) => {
    if (viewMode === 'daily') return format(d, 'MMM d');
    if (viewMode === 'weekly') return format(d, 'MMM d');
    return format(d, 'MMM yy');
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-white font-semibold text-sm">{project?.name}</span>
        <span className="text-white/50 text-xs capitalize">{viewMode} view · {fields?.join(', ')}</span>
      </div>
      {/* Header row */}
      <div className="flex ml-32 mb-1">
        {columns?.slice(0, 8)?.map((col, i) => (
          <div key={i} className="flex-1 text-xs text-slate-400 text-center truncate">{formatCol(col)}</div>
        ))}
      </div>
      {/* Gantt rows */}
      <div className="space-y-1">
        {items?.map(item => {
          const barStyle = getBarStyle(item);
          const color = typeColors?.[item?.row_type || 'deliverable'] || 'bg-slate-500';
          return (
            <div key={item?.id} className="flex items-center gap-2 h-7">
              <div className="w-32 flex-shrink-0 text-xs text-slate-300 truncate pr-2 text-right">{item?.name}</div>
              <div className="flex-1 relative h-5 bg-slate-800 rounded">
                <div className={`absolute top-0 h-full ${color} rounded opacity-90`} style={barStyle} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Canvas Element Renderer (for display) ───────────────────────────────────
const CanvasElementDisplay = ({ el, slideW, slideH, containerW, containerH }) => {
  const scaleX = containerW / slideW;
  const scaleY = containerH / slideH;
  const scale = Math.min(scaleX, scaleY);

  const left = el?.x * scaleX;
  const top = el?.y * scaleY;
  const width = el?.w * scaleX;
  const height = el?.h * scaleY;

  const style = {
    position: 'absolute',
    left, top, width, height,
    opacity: el?.opacity !== undefined ? el?.opacity : 1,
    zIndex: el?.zIndex || 1,
    overflow: 'hidden',
  };

  if (el?.type === 'text') {
    const bgColor = el?.bgColor && el?.bgColor !== 'transparent' ? el?.bgColor : undefined;
    return (
      <div style={{
        ...style,
        background: bgColor,
        display: 'flex',
        alignItems: el?.vAlign === 'top' ? 'flex-start' : el?.vAlign === 'bottom' ? 'flex-end' : 'center',
        justifyContent: el?.align === 'left' ? 'flex-start' : el?.align === 'right' ? 'flex-end' : 'center',
        padding: `${4 * scale}px`,
        boxSizing: 'border-box',
      }}>
        <span style={{
          fontFamily: el?.fontFamily || 'Inter, sans-serif',
          fontSize: `${(el?.fontSize || 24) * scale}px`,
          fontWeight: el?.bold ? 'bold' : 'normal',
          fontStyle: el?.italic ? 'italic' : 'normal',
          textDecoration: el?.underline ? 'underline' : 'none',
          color: el?.color || '#ffffff',
          textAlign: el?.align || 'center',
          lineHeight: 1.3,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          textShadow: el?.shadow ? '1px 2px 6px rgba(0,0,0,0.7)' : 'none',
        }}>
          {el?.text || ''}
        </span>
      </div>
    );
  }

  if (el?.type === 'image') {
    return (
      <div style={style}>
        {el?.url ? (
          <img
            src={el?.url}
            alt={el?.alt || ''}
            style={{
              width: '100%', height: '100%',
              objectFit: el?.objectFit || 'cover',
              display: 'block',
              borderRadius: el?.borderRadius ? `${el?.borderRadius * scale}px` : 0,
            }}
          />
        ) : null}
      </div>
    );
  }

  if (el?.type === 'shape') {
    const shapeType = el?.shapeType || 'rectangle';
    const borderRadius =
      shapeType === 'rounded' ? `${8 * scale}px` :
      shapeType === 'circle' ? '50%' : '0';
    const shapeH = shapeType === 'line' ? `${Math.max(2, (el?.lineThickness || 4) * scale)}px` : '100%';
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
      }}>
        <div style={{
          width: '100%',
          height: shapeH,
          background: el?.fill || '#f59e0b',
          borderRadius,
          border: el?.strokeWidth ? `${el?.strokeWidth * scale}px solid ${el?.stroke || '#ffffff'}` : 'none',
          boxSizing: 'border-box',
        }} />
      </div>
    );
  }

  return null;
};

// ─── Builder Slide ────────────────────────────────────────────────────────────
const BuilderSlide = ({ config, projects, allDeliverables }) => {
  const bgColor = config?.backgroundColor || '#0f172a';
  const containerRef = React.useRef(null);
  const [dims, setDims] = React.useState({ w: 1920, h: 1080 });

  React.useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (let e of entries) setDims({ w: e.contentRect.width, h: e.contentRect.height });
    });
    if (containerRef?.current) obs?.observe(containerRef?.current);
    return () => obs?.disconnect();
  }, []);

  // New canvas-based format: sections is an array of elements with type/x/y/w/h
  const rawSections = config?.sections || [];
  const isCanvasFormat = rawSections?.length > 0 && rawSections?.[0]?.x !== undefined;
  const isLegacyFormat = rawSections?.length > 0 && rawSections?.[0]?.type && ['text', 'reminder', 'images', 'gantt']?.includes(rawSections?.[0]?.type) && rawSections?.[0]?.x === undefined;

  if (rawSections?.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400" style={{ backgroundColor: bgColor }}>
        <div className="text-center">
          <Icon name="LayoutTemplate" size={60} color="#64748b" />
          <p className="mt-4 text-xl">Empty slide — add elements in Slide Management</p>
        </div>
      </div>
    );
  }

  // New canvas-based format
  if (isCanvasFormat) {
    const SLIDE_W = 960;
    const SLIDE_H = 540;
    const sortedEls = [...rawSections]?.sort((a, b) => (a?.zIndex || 1) - (b?.zIndex || 1));
    return (
      <div ref={containerRef} className="h-full w-full relative overflow-hidden" style={{ backgroundColor: bgColor }}>
        {sortedEls?.map(el => (
          <CanvasElementDisplay
            key={el?.id}
            el={el}
            slideW={SLIDE_W}
            slideH={SLIDE_H}
            containerW={dims?.w}
            containerH={dims?.h}
          />
        ))}
      </div>
    );
  }

  // Legacy section-based format (backward compat)
  return (
    <div className="h-full text-white p-8 flex flex-col gap-5 overflow-hidden" style={{ backgroundColor: bgColor }}>
      {rawSections?.map((section, idx) => (
        <div key={section?.id || idx} className={`${rawSections?.length === 1 ? 'flex-1' : ''}`}>
          {section?.type === 'text' && <TextSection config={section?.config} />}
          {section?.type === 'reminder' && <ReminderSection config={section?.config} />}
          {section?.type === 'images' && <ImagesSection config={section?.config} />}
          {section?.type === 'gantt' && (
            <GanttSection
              config={section?.config}
              projects={projects}
              allDeliverables={allDeliverables}
            />
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Soft IP Gate ─────────────────────────────────────────────────────────────
const IP_STORAGE_KEY = 'slide_display_allowed_ips';

const checkIpAccess = async () => {
  let allowedIps = [];
  try {
    allowedIps = JSON.parse(localStorage.getItem(IP_STORAGE_KEY) || '[]');
  } catch {
    allowedIps = [];
  }
  if (!Array.isArray(allowedIps) || allowedIps?.length === 0) return true;
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const { ip } = await res?.json();
    return allowedIps?.includes(ip);
  } catch {
    return true;
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr?.split('-')?.map(Number);
  return new Date(y, m - 1, d);
};

const STATUS_COLORS = {
  'Planning': 'bg-blue-500',
  'In Progress': 'bg-amber-500',
  'Completed': 'bg-green-500',
  'On Hold': 'bg-yellow-500',
  'Cancelled': 'bg-red-500',
  'Bidding': 'bg-violet-500',
};

// ─── Transition CSS ───────────────────────────────────────────────────────────
const getTransitionStyles = (animation, phase, duration, easing) => {
  const easingMap = {
    linear: 'linear',
    ease: 'ease',
    'ease-in': 'ease-in',
    'ease-out': 'ease-out',
    'ease-in-out': 'ease-in-out',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  };
  const cssEasing = easingMap?.[easing] || 'ease-in-out';
  const transition = `all ${duration}ms ${cssEasing}`;

  if (animation === 'fade') {
    return {
      entering: { opacity: 0, transition },
      active: { opacity: 1, transition },
      exiting: { opacity: 0, transition },
    }?.[phase];
  }
  if (animation === 'slide-left') {
    return {
      entering: { opacity: 0, transform: 'translateX(100%)', transition },
      active: { opacity: 1, transform: 'translateX(0)', transition },
      exiting: { opacity: 0, transform: 'translateX(-100%)', transition },
    }?.[phase];
  }
  if (animation === 'slide-right') {
    return {
      entering: { opacity: 0, transform: 'translateX(-100%)', transition },
      active: { opacity: 1, transform: 'translateX(0)', transition },
      exiting: { opacity: 0, transform: 'translateX(100%)', transition },
    }?.[phase];
  }
  if (animation === 'slide-up') {
    return {
      entering: { opacity: 0, transform: 'translateY(100%)', transition },
      active: { opacity: 1, transform: 'translateY(0)', transition },
      exiting: { opacity: 0, transform: 'translateY(-100%)', transition },
    }?.[phase];
  }
  if (animation === 'zoom') {
    return {
      entering: { opacity: 0, transform: 'scale(0.85)', transition },
      active: { opacity: 1, transform: 'scale(1)', transition },
      exiting: { opacity: 0, transform: 'scale(1.1)', transition },
    }?.[phase];
  }
  if (animation === 'zoom-out') {
    return {
      entering: { opacity: 0, transform: 'scale(1.15)', transition },
      active: { opacity: 1, transform: 'scale(1)', transition },
      exiting: { opacity: 0, transform: 'scale(0.9)', transition },
    }?.[phase];
  }
  // none
  return { opacity: 1 };
};

// ─── Slide Components ─────────────────────────────────────────────────────────

const LogoSlide = ({ config, logoUrl }) => (
  <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-amber-900 via-orange-800 to-red-900 text-white">
    <div className="flex flex-col items-center gap-8">
      {logoUrl ? (
        <img src={logoUrl} alt="Company logo" className="h-40 w-auto object-contain drop-shadow-2xl" />
      ) : (
        <div className="w-40 h-40 bg-white/20 rounded-3xl flex items-center justify-center">
          <Icon name="HardHat" size={80} color="#fff" />
        </div>
      )}
      <div className="text-center">
        <h1 className="text-6xl font-bold tracking-tight drop-shadow-lg">ConstructionPro</h1>
        {config?.subtitle && (
          <p className="text-2xl text-white/80 mt-3 font-light">{config?.subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

const ImageSlide = ({ config }) => (
  <div className="relative h-full w-full overflow-hidden bg-gray-900">
    {config?.imageUrl ? (
      <img
        src={config?.imageUrl}
        alt={config?.caption || 'Slide image'}
        className="w-full h-full object-cover"
      />
    ) : (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-700 to-gray-900">
        <Icon name="Image" size={80} color="#9ca3af" />
      </div>
    )}
    {(config?.caption || config?.title) && (
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-10">
        {config?.title && <h2 className="text-4xl font-bold text-white mb-2">{config?.title}</h2>}
        {config?.caption && <p className="text-xl text-white/80">{config?.caption}</p>}
      </div>
    )}
  </div>
);

const ProjectSummarySlide = ({ projects }) => {
  const statusCounts = projects?.reduce((acc, p) => {
    acc[p?.status] = (acc?.[p?.status] || 0) + 1;
    return acc;
  }, {});

  const activeProjects = projects?.filter(p =>
    p?.status === 'In Progress' || p?.status === 'Planning'
  );

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 text-white p-12 flex flex-col">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-white">Project Overview</h2>
        <p className="text-slate-400 text-lg mt-1">{format(new Date(), 'MMMM d, yyyy')}</p>
      </div>

      <div className="grid grid-cols-4 gap-6 mb-10">
        {[
          { label: 'Total Projects', value: projects?.length, icon: 'Briefcase', color: 'from-amber-500 to-orange-600' },
          { label: 'In Progress', value: statusCounts?.['In Progress'] || 0, icon: 'Play', color: 'from-blue-500 to-blue-600' },
          { label: 'Planning', value: statusCounts?.['Planning'] || 0, icon: 'Calendar', color: 'from-purple-500 to-purple-600' },
          { label: 'Completed', value: statusCounts?.['Completed'] || 0, icon: 'CheckCircle', color: 'from-green-500 to-green-600' },
        ]?.map(stat => (
          <div key={stat?.label} className={`bg-gradient-to-br ${stat?.color} rounded-2xl p-6 flex flex-col gap-2`}>
            <Icon name={stat?.icon} size={28} color="#fff" />
            <div className="text-5xl font-bold">{stat?.value}</div>
            <div className="text-white/80 text-sm font-medium">{stat?.label}</div>
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-hidden">
        <h3 className="text-xl font-semibold text-slate-300 mb-4">Active Projects</h3>
        <div className="grid grid-cols-2 gap-4 overflow-hidden" style={{ maxHeight: '100%' }}>
          {activeProjects?.slice(0, 8)?.map(project => {
            const start = parseLocalDate(project?.start_date);
            const end = parseLocalDate(project?.end_date);
            const today = new Date();
            const totalDays = start && end ? differenceInDays(end, start) : 0;
            let elapsed = start ? differenceInDays(today, start) : 0;
            const progress = totalDays > 0 ? Math.min(100, Math.max(0, (elapsed / totalDays) * 100)) : 0;

            return (
              <div key={project?.id} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-white text-sm leading-tight flex-1 mr-2 line-clamp-1">
                    {project?.name}
                  </h4>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                    project?.status === 'In Progress' ? 'bg-amber-500/30 text-amber-300' : 'bg-blue-500/30 text-blue-300'
                  }`}>
                    {project?.status}
                  </span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-amber-400 h-1.5 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{start ? format(start, 'MMM d') : '—'}</span>
                  <span>{Math.round(progress)}%</span>
                  <span>{end ? format(end, 'MMM d') : '—'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const GanttSlide = ({ project, deliverables }) => {
  if (!project) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-900 text-slate-400">
        <div className="text-center">
          <Icon name="BarChart2" size={60} color="#64748b" />
          <p className="mt-4 text-xl">No project selected for Gantt view</p>
        </div>
      </div>
    );
  }

  const allItems = deliverables?.filter(d => d?.startDate && d?.endDate) || [];
  if (allItems?.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-900 text-slate-400">
        <div className="text-center">
          <Icon name="BarChart2" size={60} color="#64748b" />
          <p className="mt-4 text-xl">No dated deliverables found</p>
        </div>
      </div>
    );
  }

  const dates = allItems?.flatMap(d => [parseLocalDate(d?.startDate), parseLocalDate(d?.endDate)])?.filter(Boolean);
  const minDate = new Date(Math.min(...dates?.map(d => d?.getTime())));
  const maxDate = new Date(Math.max(...dates?.map(d => d?.getTime())));
  const totalDays = Math.max(differenceInDays(maxDate, minDate) + 1, 1);

  const getBarStyle = (item) => {
    const start = parseLocalDate(item?.startDate);
    const end = parseLocalDate(item?.endDate);
    if (!start || !end) return null;
    const left = (differenceInDays(start, minDate) / totalDays) * 100;
    const width = Math.max(((differenceInDays(end, start) + 1) / totalDays) * 100, 0.5);
    return { left: `${left}%`, width: `${width}%` };
  };

  const typeColors = {
    phase: 'bg-amber-500',
    deliverable: 'bg-purple-500',
    task: 'bg-blue-500',
    subtask: 'bg-emerald-500',
  };

  const visibleItems = allItems?.slice(0, 14);

  return (
    <div className="h-full bg-slate-900 text-white p-8 flex flex-col">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white">{project?.project_name || project?.name}</h2>
        <p className="text-slate-400 mt-1">Gantt Chart · {format(new Date(), 'MMMM d, yyyy')}</p>
      </div>

      <div className="flex mb-2 ml-48">
        {[0, 0.25, 0.5, 0.75, 1]?.map(pct => {
          const d = addDays(minDate, Math.round(totalDays * pct));
          return (
            <div key={pct} className="flex-1 text-xs text-slate-500 text-center">
              {format(d, 'MMM d')}
            </div>
          );
        })}
      </div>

      <div className="flex-1 overflow-hidden space-y-1">
        {visibleItems?.map(item => {
          const barStyle = getBarStyle(item);
          const color = typeColors?.[item?.rowType] || 'bg-slate-500';
          return (
            <div key={item?.id} className="flex items-center gap-2 h-9">
              <div className="w-48 flex-shrink-0 text-sm text-slate-300 truncate pr-2 text-right">
                {item?.name}
              </div>
              <div className="flex-1 relative h-6 bg-slate-800 rounded">
                {barStyle && (
                  <div
                    className={`absolute top-0 h-full ${color} rounded opacity-90`}
                    style={barStyle}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ResourceAssignmentsSlide = ({ resources, deliverableAssignments }) => {
  const today = new Date();
  const in30Days = addDays(today, 30);

  const activeAssignments = deliverableAssignments?.filter(a => {
    const start = a?.allocationStartDate ? new Date(a?.allocationStartDate) : null;
    const end = a?.allocationEndDate ? new Date(a?.allocationEndDate) : null;
    if (!start || !end) return false;
    return start <= in30Days && end >= today;
  }) || [];

  const assignedCount = resources?.filter(r => r?.status === 'Assigned' || r?.status === 'In Use')?.length;
  const availableCount = resources?.filter(r => r?.status === 'Available')?.length;
  const maintenanceCount = resources?.filter(r => r?.status === 'Maintenance')?.length;

  const categoryColors = {
    employees: 'bg-blue-500',
    equipment: 'bg-amber-500',
    trucks: 'bg-orange-500',
    contractors: 'bg-purple-500',
    teams: 'bg-green-500',
  };

  const categoryLabels = {
    employees: 'Employees',
    equipment: 'Equipment',
    trucks: 'Trucks',
    contractors: 'Contractors',
    teams: 'Teams',
  };

  const byCategory = resources?.reduce((acc, r) => {
    const cat = r?.category || 'other';
    if (!acc?.[cat]) acc[cat] = { total: 0, assigned: 0 };
    acc[cat].total++;
    if (r?.status === 'Assigned' || r?.status === 'In Use') acc[cat].assigned++;
    return acc;
  }, {});

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 to-indigo-950 text-white p-12 flex flex-col">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-white">Resource Assignments</h2>
        <p className="text-slate-400 text-lg mt-1">{format(new Date(), 'MMMM d, yyyy')} · Next 30 Days</p>
      </div>

      <div className="grid grid-cols-3 gap-6 mb-10">
        {[
          { label: 'Assigned', value: assignedCount, color: 'from-amber-500 to-orange-600', icon: 'UserCheck' },
          { label: 'Available', value: availableCount, color: 'from-green-500 to-green-600', icon: 'UserPlus' },
          { label: 'Maintenance', value: maintenanceCount, color: 'from-red-500 to-red-600', icon: 'Wrench' },
        ]?.map(s => (
          <div key={s?.label} className={`bg-gradient-to-br ${s?.color} rounded-2xl p-6 flex items-center gap-4`}>
            <Icon name={s?.icon} size={36} color="#fff" />
            <div>
              <div className="text-5xl font-bold">{s?.value}</div>
              <div className="text-white/80 text-sm">{s?.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex-1">
        <h3 className="text-xl font-semibold text-slate-300 mb-4">Utilization by Category</h3>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(byCategory)?.map(([cat, data]) => {
            const pct = data?.total > 0 ? Math.round((data?.assigned / data?.total) * 100) : 0;
            const color = categoryColors?.[cat] || 'bg-slate-500';
            return (
              <div key={cat} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-white">{categoryLabels?.[cat] || cat}</span>
                  <span className="text-slate-300 text-sm">{data?.assigned}/{data?.total}</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-3">
                  <div
                    className={`${color} h-3 rounded-full transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-right text-xs text-slate-400 mt-1">{pct}% utilized</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Animated Slide Wrapper ───────────────────────────────────────────────────
const AnimatedSlide = ({ children, animation, duration, easing, slideKey }) => {
  const [phase, setPhase] = useState('entering');

  useEffect(() => {
    setPhase('entering');
    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => setPhase('active'));
    });
    return () => cancelAnimationFrame(t);
  }, [slideKey]);

  const style = animation === 'none'
    ? {}
    : getTransitionStyles(animation, phase, duration, easing);

  return (
    <div className="absolute inset-0 overflow-hidden" style={style}>
      {children}
    </div>
  );
};

// ─── Main Display Page ────────────────────────────────────────────────────────

const DISPLAY_CHANNEL = 'display-control';

const DisplayPage = () => {
  const [slides, setSlides] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [projects, setProjects] = useState([]);
  const [resources, setResources] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [deliverableAssignments, setDeliverableAssignments] = useState([]);
  const [timingOverride, setTimingOverride] = useState(null); // seconds, null = use slide default
  const [refreshKey, setRefreshKey] = useState(0);
  const [slideKey, setSlideKey] = useState(0);

  // Animation settings — can be overridden via control panel
  const [animSettings, setAnimSettings] = useState({
    animation: 'fade',      // fade | slide-left | slide-right | slide-up | zoom | zoom-out | none
    duration: 600,          // ms
    easing: 'ease-in-out',  // linear | ease | ease-in | ease-out | ease-in-out | spring
  });

  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const channelRef = useRef(null);

  // Check IP access
  useEffect(() => {
    checkIpAccess()?.then(allowed => {
      if (!allowed) setAccessDenied(true);
    });
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [slidesRes, logo, projectsRes, resourcesRes] = await Promise.all([
        slideService?.getActive(),
        getLogoUrl(),
        supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('resources')?.select('*, assignments:resource_assignments(*)'),
      ]);

      setSlides(slidesRes?.data || []);
      setLogoUrl(logo);
      setProjects(projectsRes?.data || []);
      setResources(resourcesRes?.data || []);

      // Load deliverables for all projects (needed for builder slides with gantt sections)
      const { data: delData } = await supabase
        ?.from('deliverables')
        ?.select('*')
        ?.order('sort_order', { ascending: true });
      setDeliverables(delData || []);

      const { data: daData } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.select('*, resource:resources(*), deliverable:deliverables(name, start_date, end_date)');
      if (daData) {
        const mapped = daData?.map(a => ({
          ...a?.resource,
          allocationStartDate: a?.start_date,
          allocationEndDate: a?.end_date,
          deliverableName: a?.deliverable?.name,
        }));
        setDeliverableAssignments(mapped);
      }
    } catch (err) {
      console.error('Display init error:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadData();
      setLoading(false);
    };
    init();
  }, [refreshKey]);

  // Realtime control channel
  useEffect(() => {
    const channel = supabase?.channel(DISPLAY_CHANNEL, {
      config: { broadcast: { self: false } },
    });

    channel?.on('broadcast', { event: 'control' }, ({ payload }) => {
        const { action, value } = payload || {};
        if (action === 'pause') setIsPaused(true);
        if (action === 'play') setIsPaused(false);
        if (action === 'toggle_pause') setIsPaused(p => !p);
        if (action === 'next') {
          setCurrentIndex(prev => {
            const next = (prev + 1) % (slides?.length || 1);
            setSlideKey(k => k + 1);
            return next;
          });
        }
        if (action === 'prev') {
          setCurrentIndex(prev => {
            const next = (prev - 1 + (slides?.length || 1)) % (slides?.length || 1);
            setSlideKey(k => k + 1);
            return next;
          });
        }
        if (action === 'goto' && typeof value === 'number') {
          setCurrentIndex(value);
          setSlideKey(k => k + 1);
        }
        if (action === 'set_timing' && typeof value === 'number') {
          setTimingOverride(value);
        }
        if (action === 'clear_timing') {
          setTimingOverride(null);
        }
        if (action === 'refresh') {
          setRefreshKey(k => k + 1);
        }
        if (action === 'set_animation') {
          setAnimSettings(prev => ({ ...prev, ...value }));
        }
      })?.subscribe();

    channelRef.current = channel;

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [slides?.length]);

  // Broadcast current state so control panel can sync
  const broadcastState = useCallback(() => {
    channelRef?.current?.send({
      type: 'broadcast',
      event: 'state',
      payload: {
        currentIndex,
        isPaused,
        timingOverride,
        totalSlides: slides?.length,
        currentSlideTitle: slides?.[currentIndex]?.title || '',
        animSettings,
      },
    });
  }, [currentIndex, isPaused, timingOverride, slides, animSettings]);

  useEffect(() => {
    broadcastState();
  }, [currentIndex, isPaused, timingOverride, animSettings]);

  // Auto-advance timer
  useEffect(() => {
    if (timerRef?.current) clearTimeout(timerRef?.current);
    if (progressRef?.current) clearInterval(progressRef?.current);
    if (slides?.length === 0 || isPaused) return;

    const currentSlide = slides?.[currentIndex];
    const durationSec = timingOverride ?? (currentSlide?.durationSeconds || 10);
    const duration = durationSec * 1000;
    const tickMs = 100;
    let elapsed = 0;

    setProgress(0);
    progressRef.current = setInterval(() => {
      elapsed += tickMs;
      setProgress(Math.min(100, (elapsed / duration) * 100));
    }, tickMs);

    timerRef.current = setTimeout(() => {
      clearInterval(progressRef?.current);
      setCurrentIndex(prev => (prev + 1) % slides?.length);
      setSlideKey(k => k + 1);
    }, duration);

    return () => {
      if (timerRef?.current) clearTimeout(timerRef?.current);
      if (progressRef?.current) clearInterval(progressRef?.current);
    };
  }, [currentIndex, isPaused, slides, timingOverride]);

  const goTo = (idx) => {
    if (timerRef?.current) clearTimeout(timerRef?.current);
    if (progressRef?.current) clearInterval(progressRef?.current);
    setCurrentIndex(idx);
    setSlideKey(k => k + 1);
  };

  const goNext = () => goTo((currentIndex + 1) % slides?.length);
  const goPrev = () => goTo((currentIndex - 1 + slides?.length) % slides?.length);

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <Icon name="Lock" size={60} color="#ef4444" />
          <h1 className="text-3xl font-bold mt-4">Access Restricted</h1>
          <p className="text-gray-400 mt-2">This display is not available from your location.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-amber-500 border-t-transparent mb-4" />
          <p className="text-xl text-gray-400">Loading display...</p>
        </div>
      </div>
    );
  }

  if (slides?.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <Icon name="Monitor" size={60} color="#6b7280" />
          <h1 className="text-3xl font-bold mt-4">No Active Slides</h1>
          <p className="text-gray-400 mt-2">Configure slides in the admin panel to get started.</p>
        </div>
      </div>
    );
  }

  const currentSlide = slides?.[currentIndex];

  const renderSlideContent = (slide) => {
    switch (slide?.slideType) {
      case 'logo':
        return <LogoSlide config={slide?.config} logoUrl={logoUrl} />;
      case 'image':
        return <ImageSlide config={slide?.config} />;
      case 'project_summary':
        return <ProjectSummarySlide projects={projects} />;
      case 'gantt': {
        const projectId = slide?.config?.projectId || projects?.[0]?.id;
        const project = projects?.find(p => p?.id === projectId);
        const projDeliverables = deliverables?.filter(d => d?.project_id === projectId)?.map(d => ({
          ...d,
          startDate: d?.start_date,
          endDate: d?.end_date,
          rowType: d?.row_type || 'deliverable',
        }));
        return <GanttSlide project={project} deliverables={projDeliverables} />;
      }
      case 'resource_assignments':
        return <ResourceAssignmentsSlide resources={resources} deliverableAssignments={deliverableAssignments} />;
      case 'builder':
        return <BuilderSlide config={slide?.config} projects={projects} allDeliverables={deliverables} />;
      default:
        return <LogoSlide config={slide?.config} logoUrl={logoUrl} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden select-none">
      {/* Animated slide content */}
      <AnimatedSlide
        animation={animSettings?.animation}
        duration={animSettings?.duration}
        easing={animSettings?.easing}
        slideKey={slideKey}
      >
        {renderSlideContent(currentSlide)}
      </AnimatedSlide>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-20">
        <div
          className="h-full bg-amber-500 transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Paused indicator */}
      {isPaused && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
          <Icon name="Pause" size={14} color="#f59e0b" />
          <span className="text-amber-400">Paused</span>
        </div>
      )}

      {/* Timing override indicator */}
      {timingOverride !== null && (
        <div className="absolute top-4 left-4 z-30 flex items-center gap-1.5 bg-black/60 text-white px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm">
          <Icon name="Clock" size={12} color="#60a5fa" />
          <span className="text-blue-300">{timingOverride}s override</span>
        </div>
      )}

      {/* Slide dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {slides?.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? 'bg-white w-6' : 'bg-white/40'
            }`}
          />
        ))}
      </div>

      {/* Controls overlay (visible on hover) */}
      <div className="absolute inset-0 z-10 flex items-center justify-between px-4 opacity-0 hover:opacity-100 transition-opacity">
        <button
          onClick={goPrev}
          className="w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white backdrop-blur-sm"
        >
          <Icon name="ChevronLeft" size={24} color="#fff" />
        </button>
        <button
          onClick={() => setIsPaused(p => !p)}
          className="w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white backdrop-blur-sm"
        >
          <Icon name={isPaused ? 'Play' : 'Pause'} size={20} color="#fff" />
        </button>
        <button
          onClick={goNext}
          className="w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white backdrop-blur-sm"
        >
          <Icon name="ChevronRight" size={24} color="#fff" />
        </button>
      </div>

      {/* Slide counter */}
      <div className="absolute top-4 right-4 z-20 text-white/50 text-sm font-medium">
        {currentIndex + 1} / {slides?.length}
        {currentSlide?.title && ` · ${currentSlide?.title}`}
      </div>
    </div>
  );
};

export default DisplayPage;
